import { useAtomValue } from 'jotai';
import { useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';

import { useTranslate } from '@growtherapy/i18n';

import { logger } from '../../datadog';
import { RETRYING_CONNECTION_INFO_TOAST_KEY, TERMINAL_CONNECTION_ERROR_TOAST_KEY } from '../../provider/sync';
import { ConnectionError, useInitTwilioSyncClient as useBaseInitTwilioSyncClient } from '../../session';
import { toast } from '../../toasts';
import { visitorNameAtom } from '../state';
import { useVisitorId } from '../useVisitorId';
import { useCreateVisitorAccessDocTokenCallback } from '../waiting-room/useCreateVisitorAccessDocTokenMutation';

export function useInitTwilioSyncClient() {
  const t = useTranslate();
  const [createTwilioVisitorAccessDocToken, { data }] = useCreateVisitorAccessDocTokenCallback();
  const visitorUuid = useVisitorId();
  const visitorName = useAtomValue(visitorNameAtom);
  const { providerShortId } = useParams() as { providerShortId: string };

  useEffect(
    function createAccessToken() {
      createTwilioVisitorAccessDocToken({
        visitorName,
        visitorUuid,
        providerShortId,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const onConnectionError = useCallback(
    (error: ConnectionError) => {
      if (error.terminal) {
        toast.close(RETRYING_CONNECTION_INFO_TOAST_KEY);
        toast.error(t('telehealth-core:errorsCore.unfortunatelyWeCantConnectYou'), {
          key: TERMINAL_CONNECTION_ERROR_TOAST_KEY,
          timeout: Infinity,
          purpose: 'system',
        });
      } else {
        logger.info('Retrying twilio sync connection');
        toast.info(t('telehealth-core:infoCore.wereWorkingOnConnectingYou'), {
          key: RETRYING_CONNECTION_INFO_TOAST_KEY,
          timeout: Infinity,
          purpose: 'system',
        });
      }
    },
    [t],
  );

  useBaseInitTwilioSyncClient({
    jwt: data?.createTwilioVisitorAccess?.jwt,
    onConnectionError,
  });
}
